<template>
  <form
    :id="id"
    class="shipping-point-location-edit"
    autocomplete="off"
    tabindex="-1"
  >
    <div class="row">
      <InputWrapper
        v-slot="{ ariaProps, errors, inputClasses, inputId }"
        :errors="v$.locationName.$errors"
        :input-id="`${id}-name`"
        :label="t('common.paramName', { param: t('locations.locations') })"
        class="w-3"
      >
        <LocationQuickSearch
          :id="inputId"
          v-model="searchModel"
          v-bind="ariaProps"
          :type="locationType"
          :errors="errors"
          :class="inputClasses"
          :append-to="appendTo"
          :disabled="disabled"
          :placeholder="
            t('common.paramName', { param: t('locations.locations') })
          "
          search-field="name"
          include-trimble-results
          @clear="emit('clear')"
          @select="onItemSelected($event, true)"
        />
      </InputWrapper>
      <InputWrapper
        v-slot="{ ariaProps, errors, inputClasses, inputId }"
        :errors="v$.addressLine1.$errors"
        :input-id="`${id}-line1`"
        :label="t('address.addressLineNum', { num: 1 })"
        class="w-3"
      >
        <LocationQuickSearch
          :id="inputId"
          v-model="searchModel"
          v-bind="ariaProps"
          :type="locationType"
          :errors="errors"
          :class="inputClasses"
          :append-to="appendTo"
          :disabled="disabled || isUneditableFiconLocation"
          :placeholder="t('address.addressLineNum', { num: 1 })"
          search-field="addressLine1"
          include-trimble-results
          @clear="emit('clear')"
          @select="onItemSelected"
        />
      </InputWrapper>
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :errors="v$.addressLine2.$errors"
        :input-id="`${id}-line2`"
        :label="t('address.addressLineNum', { num: 2 })"
        class="w-3"
        optional
      >
        <InputText
          :id="inputId"
          v-model="internalValue.addressLine2"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :disabled="disabled || isUneditableFiconLocation"
          :placeholder="t('address.addressLineNum', { num: 2 })"
        />
      </InputWrapper>
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :errors="v$.addressLine3.$errors"
        :input-id="`${id}-line3`"
        :label="t('address.addressLineNum', { num: 3 })"
        class="w-3"
        optional
      >
        <InputText
          :id="inputId"
          v-model="internalValue.addressLine3"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :disabled="disabled || isUneditableFiconLocation"
          :placeholder="t('address.addressLineNum', { num: 3 })"
        />
      </InputWrapper>
    </div>

    <div class="row">
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :errors="v$.city.$errors"
        :input-id="`${id}-city`"
        :label="t('address.city')"
        class="w-3"
      >
        <InputText
          :id="inputId"
          v-model="v$.city.$model"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :disabled="disabled || isUneditableFiconLocation"
          :placeholder="t('address.city')"
        />
      </InputWrapper>
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :errors="v$.state.$errors"
        :input-id="`${id}-state`"
        :label="t('address.state')"
        class="w-3"
      >
        <InputRegion
          :id="inputId"
          v-model="v$.state.$model"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :country="internalValue.country"
          :append-to="appendTo"
          :disabled="disabled || isUneditableFiconLocation"
        />
      </InputWrapper>
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :errors="v$.postalCode.$errors"
        :help-text="postalCodeInfo?.helpText"
        :input-id="`${id}-postal-code`"
        :label="t('address.postalCode')"
        class="w-3"
      >
        <InputText
          :id="inputId"
          v-model="v$.postalCode.$model"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :disabled="disabled || isUneditableFiconLocation"
          :placeholder="t('address.postalCode')"
        />
      </InputWrapper>
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :errors="v$.country.$errors"
        :input-id="`${id}-country`"
        :label="t('address.country')"
        class="w-3"
      >
        <CountryDropdown
          :id="inputId"
          v-model="v$.country.$model"
          v-bind="ariaProps"
          :class="inputClasses"
          :append-to="appendTo"
          :invalid="invalid"
          :disabled="disabled || isUneditableFiconLocation"
        />
      </InputWrapper>
    </div>

    <div class="row">
      <InputWrapper
        v-slot="{ ariaProps, inputId }"
        :input-id="`${id}-reference-number`"
        :label="t('common.paramCodes', { param: t('locations.locations') })"
        class="w-3"
        optional
      >
        <LocationQuickSearch
          :id="inputId"
          v-model="searchModel"
          v-bind="ariaProps"
          :type="locationType"
          :append-to="appendTo"
          :disabled="disabled || isFiconLocation"
          :placeholder="
            t('common.paramCodes', { param: t('locations.locations') })
          "
          search-field="code"
          @clear="emit('clear')"
          @select="onItemSelected"
        />
      </InputWrapper>

      <InputWrapper
        v-if="isLocationGroupingEnabled"
        v-slot="{ inputId }"
        :input-id="`${id}-location-groups`"
        :label="t('locationGroups.locationGroups')"
        class="w-3"
        optional
      >
        <LocationGroupField
          :id="inputId"
          v-model="internalValue.locationGroupIds"
          :disabled="disabled || isUneditableFiconLocation"
          :placeholder="t('locationGroups.locationGroups')"
          @saving="emit('saving', $event)"
        />
      </InputWrapper>

      <slot
        name="extra-input"
        :disabled="disabled"
        :is-uneditable-ficon-location="isUneditableFiconLocation"
      />
    </div>
  </form>
</template>

<script lang="ts" setup>
import { ValidationRule } from '@vuelidate/core';
import { CancelTokenSource, default as axios } from 'axios';
import isEqual from 'lodash/isEqual';
import { computed, nextTick, PropType, ref, Ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { matches, required, useValidations } from '@rygen/rygen-ui/composables';
import {
  Address,
  Loadable,
  Location,
  LocationTypeWellKnownNaturalId,
  POSTAL_CODE_INFOS,
} from '@rygen/rygen-ui/model';

import { useLocationGrouping } from '@/composables';
import { EmbeddableLocation } from '@/model';

import LocationGroupField from '@/components/location/LocationGroupField.vue';
import LocationQuickSearch, {
  SupportedLocationType,
} from '@/components/location/LocationQuickSearch.vue';
import CountryDropdown from '@rygen/rygen-ui/components/form/CountryDropdown.vue';
import InputRegion from '@rygen/rygen-ui/components/form/InputRegion.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import InputText from 'primevue/inputtext';

export type GroupedLocations = Array<{ label: string; items: Location[] }>;

const props = defineProps({
  modelValue: {
    type: EmbeddableLocation,
    default: () => new EmbeddableLocation(),
  },
  id: {
    type: String,
    required: true,
  },
  locationType: {
    type: String as PropType<SupportedLocationType>,
    default: () => LocationTypeWellKnownNaturalId.SHIPPING_POINT,
  },
  appendTo: {
    type: String as PropType<'body' | 'self'>,
    default: () => 'self',
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  allowFiconUpdates: {
    type: Boolean,
    default: () => false,
  },
});
const { modelValue, id, locationType, appendTo, disabled, allowFiconUpdates } =
  toRefs(props);
const emit = defineEmits<{
  (event: 'address-book-select', location: Location): void;
  (event: 'autocomplete', isAutocompleteSelection: boolean): void;
  (event: 'clear'): void;
  (event: 'saving', saving: boolean): void;
  (event: 'update:model-value', modelValue: EmbeddableLocation): void;
}>();

defineSlots<{
  'extra-input': (props: {
    disabled: boolean;
    isUneditableFiconLocation: boolean;
  }) => any;
}>();

const { t } = useI18n();
const { isLocationGroupingEnabled } = useLocationGrouping();

const internalValue = ref(new EmbeddableLocation(modelValue.value));

const searchModel = computed({
  get() {
    return internalValue.value.toLocation();
  },
  set(newVal) {
    internalValue.value = EmbeddableLocation.fromLocation(newVal);
  },
});

const hasLocationUpdate = computed(() =>
  authorization.hasAuthority('location', 'update'),
);

const isFiconLocation = computed(() => !!internalValue.value.locationId);

const isUneditableFiconLocation = computed(() => {
  return allowFiconUpdates.value
    ? !hasLocationUpdate.value
    : isFiconLocation.value;
});

const postalCodeInfo = computed(() => {
  return POSTAL_CODE_INFOS.find(
    (p) => p.countryCode === internalValue.value.country,
  );
});

const rules = computed(() => {
  const postalCodeLabel = t('address.postalCode');
  const r: Record<string, Record<string, ValidationRule>> = {
    locationName: {
      required: required(
        t('common.paramName', { param: t('locations.locations') }),
      ),
    },
    addressLine1: {
      required: required(t('address.addressLineNum', { num: 1 })),
    },
    addressLine2: {},
    addressLine3: {},
    city: {
      required: required(t('address.city')),
    },
    state: {
      required: required(t('address.state')),
    },
    postalCode: {
      required: required(postalCodeLabel),
    },
    country: {
      required: required(t('address.country')),
    },
  };

  if (postalCodeInfo.value) {
    r.postalCode.matches = matches(postalCodeInfo.value.regex, postalCodeLabel);
  }

  return r;
});
const { v$ } = useValidations(internalValue, rules);
const suggestions: Ref<Loadable<GroupedLocations>> = ref({ loading: false });
const cancelRequest: Ref<CancelTokenSource | undefined> = ref(undefined);
const isAutocompleteSelection = ref(false);

const isAddressBookLocation = (location: Location): boolean => {
  // Address book locations have IDs, Trimble results don't
  return !!location.id;
};

const onItemSelected = (item: Location, clearLocationName = false) => {
  if (isAddressBookLocation(item)) {
    emit('address-book-select', item);
  } else {
    // Preserve location name and code if this is a Trimble result
    const newLocation = EmbeddableLocation.fromLocation(item);
    if (!clearLocationName) {
      newLocation.locationName = internalValue.value.locationName;
    }
    newLocation.code = internalValue.value.code;

    nextTick(() => (internalValue.value = newLocation));
  }

  isAutocompleteSelection.value = true;
};

const cancelExistingAutocompleteRequest = () => {
  if (cancelRequest.value) {
    cancelRequest.value.cancel();
    suggestions.value.data = undefined;
  }
  cancelRequest.value = axios.CancelToken.source();
};

watch(
  modelValue,
  (opts: Partial<EmbeddableLocation>) => {
    internalValue.value = new EmbeddableLocation(opts);
  },
  { deep: true, immediate: true },
);

watch(
  internalValue,
  (newVal: EmbeddableLocation) => {
    if (!isEqual(modelValue.value, newVal)) {
      emit('update:model-value', newVal);
    }
  },
  { deep: true },
);

watch(
  () => internalValue.value.toAddress(),
  (newVal: Address) => {
    // Compare just the address fields, so locationName and code don't affect
    // autocomplete event
    if (!isEqual(newVal, modelValue.value.toAddress())) {
      emit('autocomplete', isAutocompleteSelection.value);
      nextTick(() => {
        internalValue.value.hasBeenValidated = false;
        isAutocompleteSelection.value = false;
      });
    }
  },
  { deep: true },
);

watch(
  () => internalValue.value.country,
  (_newVal?: string) => {
    v$.value.postalCode.$reset();
  },
);

defineExpose({ cancelExistingAutocompleteRequest });
</script>

<style>
.shipping-point-location-edit {
  display: flex;
  flex-direction: column;
  gap: var(--gap-1);

  .row {
    display: flex;
    gap: var(--gap-1);
  }
}
</style>
